import type { Feature, Point as GeoJsonPoint, LineString, MultiLineString } from 'geojson';
import { useMemo } from 'react';
import { BsSignpost } from 'react-icons/bs';

import type { InfrastructureFeatureProperties, InfrastructureResponse } from '~api/services';
import { Polyline, SvgTooltip, useSvgContext } from '../components';
import { useLayerTooltip } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type InfrastructureLayerProps = {
  /** Infrastructure GeoJSON feature collection */
  infrastructureSites: InfrastructureResponse;
};

type InfrastructureLineFeature = Feature<LineString | MultiLineString, InfrastructureFeatureProperties>;
type InfrastructurePointFeature = Feature<GeoJsonPoint, InfrastructureFeatureProperties>;
type InfrastructureFeature = InfrastructureLineFeature | InfrastructurePointFeature;

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const getInfrastructureFeatureId = (f: InfrastructureFeature) => String(f.properties.OBJECTID);
const TOOLTIP_CONTENT: { label: string; value: (feature: InfrastructureFeature) => string }[] = [
  {
    label: 'Layer',
    value: (f) => f.properties.layer_name,
  },
  {
    label: 'Distance',
    value: (f) => (f.properties.distance != null ? `${f.properties.distance.toFixed(2)} m` : 'N/A'),
  },
  {
    label: 'Source',
    value: (f) => f.properties.source || 'N/A',
  },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer to render infrastructure features (pipelines, roads, etc.) as polylines on the canvas.
 * Handles both LineString and MultiLineString geometries. Other geometry types are skipped.
 *
 * @example
 * ```tsx
 * <InfrastructureLayer infrastructureSites={infrastructureSites} />
 * ```
 */
const InfrastructureLayer = ({ infrastructureSites }: InfrastructureLayerProps) => {
  const { transformer } = useSvgContext();

  /**
   * Split features by geometry type in a single pass.
   * Point features (e.g. SAP assets, equipment) are rendered as circles;
   * LineString / MultiLineString features (e.g. pipelines, roads) are rendered as polylines.
   * Other geometry types (Polygon, etc.) are ignored.
   */
  const { lineFeatures, pointFeatures, allFeatures } = useMemo(() => {
    const lineFeatures: InfrastructureLineFeature[] = [];
    const pointFeatures: InfrastructurePointFeature[] = [];

    for (const f of infrastructureSites.features) {
      if (f.geometry == null) continue;
      if (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString') {
        lineFeatures.push(f as InfrastructureLineFeature);
      } else if (f.geometry.type === 'Point') {
        pointFeatures.push(f as InfrastructurePointFeature);
      }
    }

    return { lineFeatures, pointFeatures, allFeatures: [...lineFeatures, ...pointFeatures] };
  }, [infrastructureSites.features]);

  const { layerRef, tooltipRef, hoveredItem } = useLayerTooltip({
    items: allFeatures,
    getId: getInfrastructureFeatureId,
  });

  if (!transformer || allFeatures.length === 0) return null;

  return (
    <>
      <g className="infrastructure-layer" ref={layerRef} aria-label="Infrastructure layer">
        {/* Render line features (pipelines, roads, etc.) */}
        {lineFeatures.map((feature) => {
          const { OBJECTID, Name } = feature.properties;

          return (
            <g
              key={OBJECTID}
              className="infrastructure-feature hover:cursor-pointer"
              data-item-id={String(OBJECTID)}
              aria-label={`Infrastructure: ${Name ?? OBJECTID}`}
            >
              <Polyline
                geometry={feature.geometry}
                className="infrastructure-feature transition-[stroke] duration-200 ease-in-out hover:cursor-pointer hover:[stroke-opacity:1]"
                stroke="var(--color-violet-600)"
                strokeOpacity={0.5}
              />
            </g>
          );
        })}
        {/* Render point features (SAP assets, equipment, etc.) */}
        {pointFeatures.map((feature) => {
          const { OBJECTID, PLTXT, Name } = feature.properties;
          const [geoX, geoY] = feature.geometry.coordinates;
          const { x, y } = transformer.toSVG(geoX, geoY);

          return (
            <g
              key={OBJECTID}
              className="infrastructure-feature hover:cursor-pointer"
              data-item-id={String(OBJECTID)}
              aria-label={`Infrastructure: ${PLTXT ?? Name ?? OBJECTID}`}
            >
              <circle
                cx={x}
                cy={y}
                r={4}
                fill="var(--color-violet-600)"
                fillOpacity={0.7}
                stroke="var(--color-violet-900)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                className="transition-[fill-opacity] duration-200 ease-in-out hover:cursor-pointer hover:[fill-opacity:1]"
              />
            </g>
          );
        })}
      </g>
      <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
        {hoveredItem && (
          <div className="flex flex-col">
            <h4 className="flex items-center gap-1 rounded-t-md bg-muted px-3 py-2 font-medium text-base">
              <BsSignpost size={16} />
              {hoveredItem.properties.PLTXT ?? hoveredItem.properties.Name ?? `ID: ${hoveredItem.properties.OBJECTID}`}
            </h4>
            <div className="flex flex-col gap-2 rounded-b-md px-3 py-2">
              {TOOLTIP_CONTENT.map(({ label, value }) => (
                <div key={label} className="flex items-center gap-1 text-sm">
                  <span className="font-bold">{label}:</span>
                  <span>{value(hoveredItem)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SvgTooltip>
    </>
  );
};
InfrastructureLayer.displayName = 'InfrastructureLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { InfrastructureLayer, type InfrastructureLayerProps };
